import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, Tooltip, Button, Stack } from "@mui/material";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import dataConstant from "../constants/dataConstants";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

const CustomMultipleImageUpload = ({
  label,
  supportedFormats,
  onImageChange,
  images = [],
  deleteImage,
  isDeletable = false,
  maxSize,
  isImageValid = () => {},
  maxImages = 1,
  onError = () => {},
  showInternalErrors = false,
  isDownloadable = false,
}) => {
  const fileInputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedImages([...(images || [])]);
  }, [images, images?.length]);

  const handleFileChange = (event) => {
    const files = Array.from(event?.target?.files || []);
    if (files.length === 0) return;

    if (files.length + selectedImages.length > maxImages) {
      const errorMessage = t("Common.MaxImageLimit", { count: maxImages });
      handleError(errorMessage, "maxImages");
      event.target.value = "";
      return;
    }

    const maxFileSize = maxSize * 1024 * 1024;
    const allowedFormats = supportedFormats.map((f) => f.toLowerCase());
    const validFiles = [];
    const newImages = [];
    const errors = [];

    files.forEach((file, index) => {
      if (file.size > maxFileSize) {
        const errorMessage = t("Common.fileUploadLimitExceeded", {
          MaxFileSize: dataConstant.LeadCollections.MAX_FILE_SIZE_MB,
        });
        errors.push({
          fileName: file.name,
          error: errorMessage,
          type: "fileSize",
        });
        return;
      }

      const extension = file.name.split(".").pop().toLowerCase();
      if (!allowedFormats.includes(extension)) {
        const errorMessage = t("Common.InvalidFileType");
        errors.push({
          fileName: file.name,
          error: errorMessage,
          type: "fileType",
        });
        return;
      }

      const newImage = {
        id: Date.now() + index,
        images: [URL.createObjectURL(file)],
        originalFileName: file.name,
        fileExtension: extension,
        file,
      };

      validFiles.push(file);
      newImages.push(newImage);
    });

    if (errors.length > 0) {
      const errorData = {
        message: errors[0].error,
        type: "fileValidation",
        details: errors,
        timestamp: new Date().toISOString(),
      };
      handleError(errorData.message, errorData.type);
      event.target.value = "";
      return;
    }

    if (validFiles.length > 0) {
      try {
        const updatedImages = [...selectedImages, ...newImages];
        setSelectedImages(updatedImages);
        onImageChange(validFiles);
        clearError();
      } catch (error) {}
    }

    event.target.value = "";
  };

  const handleError = (errorMessage, errorType = "validation") => {
    const errorData = {
      message: errorMessage,
      type: errorType,
      timestamp: new Date().toISOString(),
    };

    onError(errorData);

    if (showInternalErrors) {
      setImageError(errorMessage);
    } else {
      setImageError("");
    }

    isImageValid(false);
  };

  const clearError = () => {
    onError(null);
    setImageError("");
    isImageValid(true);
  };
  const handleRemoveImage = (imageToRemove) => {
    if (imageToRemove?.id) {
      deleteImage(imageToRemove.id);
    }
  };

  const handleDragAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };
  const handleDownload = async (url, name = "file") => {
    const b = await (await fetch(url)).blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(b);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="cm-customimage-upload mb-24">
      <Grid container width={"100%"} spacing={2}>
        <Grid item xs={12} width={"100%"} textAlign={"center"}>
          <Grid item xs={12}>
            {label && (
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                {label}
              </Typography>
            )}
          </Grid>
          {selectedImages.length > 0 && (
            <Grid item xs={12}>
              <Box>
                <Box
                  className="cm-multiupload-inner"
                  display="flex"
                  gap={"20px"}
                  justifyContent="center"
                  width={"100%"}
                  mb={"20px"}
                >
                  {selectedImages.map((image) => {
                    const imageUrl =
                      image?.id > 0 ? image.images : image.images[0];
                    return (
                      <Box
                        key={image.id}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={"100%"}
                        justifyContent={"space-between"}
                        pb={"10px"}
                        borderBottom={"1px solid var(--black)"}
                        position={"relative"}
                        sx={{
                          background: "var(--headerbg) !important",
                          borderRadius: "0px !important",
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={"center"}
                          gap={"20px"}
                          width={"100%"}
                        >
                          <Tooltip title={image.originalFileName || ""}>
                            {image.fileExtension?.toLowerCase() !== "pdf" ? (
                              <img
                                src={imageUrl}
                                alt={image.originalFileName || "uploaded"}
                                className="cm-upload-multiple-images"
                              />
                            ) : (
                              <img
                                className="cm-upload-multiple-images"
                                width={"100%"}
                                alt={image.originalFileName || "uploaded"}
                                src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/pdf-icon.png`}
                              />
                            )}
                          </Tooltip>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color="secondary.text"
                            textAlign={{ xs: "center", sm: "left" }}
                            sx={{
                              wordBreak: { xs: "break-word", sm: "normal" },
                            }}
                          >
                            {image.originalFileName}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          gap={"10px"}
                          alignItems={"center"}
                          className="cm-upload-actions"
                        >
                          {isDownloadable && (
                            <Button
                              component="a"
                              onClick={() =>
                                handleDownload(imageUrl, image.originalFileName)
                              }
                              variant="contained"
                              startIcon={
                                <DownloadIcon
                                  sx={{ width: "25px", height: "25px" }}
                                />
                              }
                              sx={{
                                px: 1.5,
                                py: 1.5,
                                background: "var(--secondary-text)!important",
                                color: "var(--black)",
                                margin: 0,
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                            ></Button>
                          )}
                          {!isDeletable && (
                            <Button
                              onClick={() => handleRemoveImage(image)}
                              variant="contained"
                              sx={{
                                px: 1.5,
                                py: 1.5,
                                background: "var(--red)!important",
                                color: "var(--white)",
                                margin: 0,
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                              startIcon={
                                <DeleteOutlinedIcon
                                  sx={{
                                    width: "25px",
                                    height: "25px",
                                    color: "var(--white)!important",
                                  }}
                                />
                              }
                              i
                            ></Button>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          )}
          <div
            className="drag-area"
            onClick={isDeletable ? null : handleDragAreaClick}
            onDragOver={isDeletable ? null : onDragOver}
            onDrop={isDeletable ? null : onDrop}
            style={{
              borderRadius: "8px",
              textAlign: "center",
              cursor: isDeletable ? "default" : "pointer",
            }}
          >
            <Button
              variant="contained"
              tabIndex={-1}
              startIcon={<FolderCopyOutlinedIcon />}
              color="secondary"
              sx={{ px: 3, flexWrap: "wrap" }}
            >
              {t("Common.Select files")}
            </Button>
            <Typography
              variant="body1"
              color="secondary.light"
              fontWeight={500}
              mt={"20px"}
              textAlign={"center"}
            >
              {t("Common.Upload files")}{" "}
              <Typography
                variant="body1"
                component={"span"}
                color="secondary.light"
                fontWeight={500}
                sx={{ textDecoration: "underline" }}
                mb={1}
              >
                {t("Common.Choose files")}
              </Typography>
            </Typography>
            {!isDeletable && (
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                accept={supportedFormats.map((f) => "." + f).join(",")}
                onChange={handleFileChange}
                multiple={maxImages > 1}
              />
            )}
            <Typography color="error" variant="body2">
              {imageError}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default CustomMultipleImageUpload;
